import {
  Autocomplete,
  Button,
  Flex,
  Grid,
  Label,
  SelectField,
  TextAreaField,
  TextField,
} from "@aws-amplify/ui-react";
import { API, graphqlOperation } from "aws-amplify";
import { useContext, useEffect, useState } from "react";
import LocalizedStrings from "react-localization";
import toast from "react-hot-toast";

import useErrors from "hooks/useErrors";
import LabelRequired from "components/utils/LabelRequired";
import { globalStrings } from "config/translations/es";
import { LayoutContext } from "layout/context/LayoutContext";
import {
  equipmentByCompanyID,
  listCompanies,
  serviceOrdersByCompanyID,
} from "graphql/queries";

const SpareForm = ({ onSubmit, type = "create", initialValues }) => {
  const { me } = useContext(LayoutContext);
  const [spareForm, setSpareForm] = useState(initialValues);
  const [companies, setCompanies] = useState({
    list: [],
    loading: false,
  });
  const [serviceOrders, setServiceOrders] = useState({
    list: [],
    loading: false,
  });
  const [equipments, setEquipments] = useState({
    list: [],
    loading: false,
  });
  const [errors, setErrors] = useState({
    name: "",
    companyID: "",
  });
  const validateErrors = useErrors({
    name: "isRequired",
    companyID: "isRequired",
  });

  const onSubmitForm = () => {
    let hasErrors = false;

    Object.entries(spareForm).forEach((itemForm) => {
      const [name, value] = itemForm;
      const check = !!checkErrors(name, value);
      if (!hasErrors) hasErrors = check;
    });

    if (
      checkIncorrectValue("companyID", companies.list) ||
      (spareForm.serviceOrderID &&
        checkIncorrectValue("serviceOrderID", serviceOrders.list)) ||
      (spareForm.equipmentID &&
        checkIncorrectValue("equipmentID", equipments.list))
    )
      return;

    if (!hasErrors)
      onSubmit({
        ...spareForm,
        serviceOrderID: spareForm.serviceOrderID || null,
        equipmentID: spareForm.equipmentID || null,
      });
  };

  const checkIncorrectValue = (name, list) => {
    if (!list.some((rec) => rec.id === spareForm[name])) {
      setErrors((prevSt) => ({
        ...prevSt,
        [name]: strings.validations.incorrectValue,
      }));
      return true;
    }
    return false;
  };

  const onChange = (e) => {
    let { name, value } = e.target;
    setSpareForm((prevSt) => ({
      ...prevSt,
      [name]: value,
    }));
    checkErrors(name, value);
  };

  const onChangeSelect = (e) => {
    const { name, value } = e.target;
    if (name === "companyID") {
      fetchCompanies({ name: value });
      onClearSelect("serviceOrderID");
      onClearSelect("equipmentID");
    }
    if (name === "serviceOrderID")
      fetchServiceOrders({ name: value }, spareForm.companyID);
    if (name === "equipmentID")
      fetchEquipments({ name: value }, spareForm.companyID);
    setSpareForm((prevSt) => ({
      ...prevSt,
      [name]: value,
    }));
    checkErrors(name, value);
  };

  const onSelect = (name, value) => {
    setSpareForm((prevSt) => ({
      ...prevSt,
      [name]: value,
    }));
    checkErrors(name, value);
    if (name === "companyID") {
      onClearSelect("serviceOrderID", value);
      onClearSelect("equipmentID", value);
    }
  };

  const onClearSelect = (name, parentId) => {
    const value = "";
    if (name === "companyID") {
      fetchCompanies({ name: value });
      onClearSelect("serviceOrderID");
      onClearSelect("equipmentID");
    }
    if (name === "serviceOrderID") {
      if (!parentId) setServiceOrders({ list: [], loading: false });
      else fetchServiceOrders({ name: value }, parentId);
    }
    if (name === "equipmentID") {
      if (!parentId) setEquipments({ list: [], loading: false });
      else fetchEquipments({ name: value }, parentId);
    }
    setSpareForm((prevSt) => ({
      ...prevSt,
      [name]: value,
    }));
  };

  const checkErrors = (name, value) => {
    const errorMessage = validateErrors(name, value);
    if (errorMessage)
      setErrors((prevErr) => ({ ...prevErr, [name]: errorMessage }));
    else setErrors((prevErr) => ({ ...prevErr, [name]: "" }));
    return errorMessage;
  };

  const resetFormValues = () => {
    setErrors({
      name: "",
      companyID: "",
    });
    setSpareForm(initialValues);
    fetchCompanies({ id: initialValues.companyID });
    fetchServiceOrders(
      { id: initialValues.serviceOrderID },
      initialValues.companyID
    );
    fetchEquipments({ id: initialValues.equipmentID }, initialValues.companyID);
  };

  async function fetchCompanies({ id = "", name = "" }) {
    let filterObj = {};
    try {
      setCompanies({ list: [], loading: true });

      if (me.companyID)
        filterObj = { ...filterObj, or: [{ id: { eq: me.companyID } }] };
      if (me.managedCompanies.length > 0)
        filterObj = {
          ...filterObj,
          or: [
            ...(filterObj?.or ?? []),
            ...me.managedCompanies.map((cp) => ({ id: { eq: cp } })),
          ],
        };

      const companiesData = await API.graphql(
        graphqlOperation(listCompanies, {
          limit:
            !id && !name && Object.keys(filterObj).length <= 0 ? 20 : undefined,
          filter: {
            and: [
              {
                id: { contains: id },
              },
              {
                name: { contains: name },
              },
            ],
            ...filterObj,
          },
        })
      );
      const companiesItems = companiesData.data.listCompanies.items;
      setCompanies((prev) => ({ ...prev, list: companiesItems }));
    } catch (err) {
      toast.error(strings.responses.errorFetchCompanies);
    } finally {
      setCompanies((prev) => ({ ...prev, loading: false }));
    }
  }

  async function fetchServiceOrders({ id = "", name = "" }, companyID) {
    try {
      if (!companyID) return;
      setServiceOrders({ list: [], loading: true });
      const equipmentsData = await API.graphql(
        graphqlOperation(serviceOrdersByCompanyID, {
          companyID,
          limit: !id && !name ? 20 : undefined,
          filter: {
            and: [
              {
                id: { contains: id || "" },
              },
              {
                code: { contains: name },
              },
            ],
            deletedAt: { attributeExists: false },
          },
        })
      );
      const equipmentsItems =
        equipmentsData.data.serviceOrdersByCompanyID.items;
      setServiceOrders((prev) => ({ ...prev, list: equipmentsItems }));
    } catch (err) {
      toast.error(strings.responses.errorFetchServiceOrders);
    } finally {
      setServiceOrders((prev) => ({ ...prev, loading: false }));
    }
  }

  async function fetchEquipments({ id = "", name = "" }, companyID) {
    try {
      if (!companyID) return;
      setEquipments({ list: [], loading: true });
      const equipmentsData = await API.graphql(
        graphqlOperation(equipmentByCompanyID, {
          companyID,
          limit: 500,
          filter: {
            and: [
              {
                id: { contains: id || "" },
              },
              {
                name: { contains: name },
              },
            ],
            deletedAt: { attributeExists: false },
          },
        })
      );
      const equipmentsItems = equipmentsData.data.equipmentByCompanyID.items;
      setEquipments((prev) => ({ ...prev, list: equipmentsItems }));
    } catch (err) {
      toast.error(strings.responses.errorFetchEquipments);
    } finally {
      setEquipments((prev) => ({ ...prev, loading: false }));
    }
  }

  useEffect(() => {
    setSpareForm(initialValues);
    fetchCompanies({ id: initialValues.companyID });
    fetchServiceOrders(
      { id: initialValues.serviceOrderID },
      initialValues.companyID
    );
    fetchEquipments({ id: initialValues.equipmentID }, initialValues.companyID);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValues]);

  return (
    <>
      <Grid rowGap="15px" columnGap="15px" padding="20px">
        <Grid
          templateColumns={{ base: "1fr", large: "2fr 2fr" }}
          rowGap="15px"
          columnGap="15px"
        >
          <TextField
            name="name"
            label={<LabelRequired text={strings.fields.name.label} />}
            value={spareForm.name}
            onChange={onChange}
            errorMessage={errors.name}
            hasError={!!errors.name}
          ></TextField>
          <TextField
            name="location"
            label={strings.fields.location.label}
            value={spareForm.location}
            onChange={onChange}
          ></TextField>
          <SelectField
            name="status"
            label={strings.fields.status.label}
            onChange={onChange}
            value={spareForm.status ?? ""}
          >
            <option value="">{globalStrings.selectOption}</option>
            {Object.entries(globalStrings.spareStatus).map((opt) => (
              <option value={opt[0]}>{opt[1]}</option>
            ))}
          </SelectField>
          {type === "create" && (
            <Flex direction="column" style={styles.autocomplete}>
              <Label htmlFor="companyID">
                <LabelRequired text={strings.fields.company.label} />
              </Label>
              <Autocomplete
                id="companyID"
                name="companyID"
                isRequired
                placeholder={strings.fields.company.placeholder}
                value={
                  companies.list.find(
                    (companyRec) => companyRec.id === spareForm.companyID
                  )?.name ?? spareForm.companyID
                }
                options={companies.list.map((companyRec) => ({
                  id: companyRec.id,
                  label: companyRec.name,
                }))}
                isLoading={companies.loading}
                onClear={() => onClearSelect("companyID")}
                onChange={onChangeSelect}
                onSelect={({ id }) => onSelect("companyID", id)}
                errorMessage={errors.companyID}
                hasError={!!errors.companyID}
                menuSlots={{ Empty: globalStrings.noResults }}
              />
            </Flex>
          )}
          <Flex direction="column" style={styles.autocomplete}>
            <Label htmlFor="serviceOrderID">
              {strings.fields.serviceOrder.label}
            </Label>
            <Autocomplete
              id="serviceOrderID"
              name="serviceOrderID"
              disabled={!spareForm.companyID}
              placeholder={strings.fields.serviceOrder.placeholder}
              value={
                serviceOrders.list.find(
                  (serviceOrderRec) =>
                    serviceOrderRec.id === spareForm.serviceOrderID
                )?.code ||
                spareForm.serviceOrderID ||
                ""
              }
              options={serviceOrders.list.map((serviceOrderRec) => ({
                id: serviceOrderRec.id,
                label: serviceOrderRec.code,
              }))}
              isLoading={serviceOrders.loading}
              onClear={() =>
                onClearSelect("serviceOrderID", spareForm.companyID)
              }
              onChange={onChangeSelect}
              onSelect={({ id }) => onSelect("serviceOrderID", id)}
              errorMessage={errors.serviceOrderID}
              hasError={!!errors.serviceOrderID}
              menuSlots={{ Empty: globalStrings.noResults }}
            />
          </Flex>
          <Flex direction="column" style={styles.autocomplete}>
            <Label htmlFor="equipmentID">
              {strings.fields.equipment.label}
            </Label>
            <Autocomplete
              id="equipmentID"
              name="equipmentID"
              disabled={!spareForm.companyID}
              placeholder={strings.fields.equipment.placeholder}
              value={
                equipments.list.find(
                  (equipmentRec) => equipmentRec.id === spareForm.equipmentID
                )?.name ||
                spareForm.equipmentID ||
                ""
              }
              options={equipments.list.map((equipmentRec) => ({
                id: equipmentRec.id,
                label: equipmentRec.name,
              }))}
              isLoading={equipments.loading}
              onClear={() => onClearSelect("equipmentID", spareForm.companyID)}
              onChange={onChangeSelect}
              onSelect={({ id }) => onSelect("equipmentID", id)}
              errorMessage={errors.equipmentID}
              hasError={!!errors.equipmentID}
              menuSlots={{ Empty: globalStrings.noResults }}
            />
          </Flex>
        </Grid>
        <TextAreaField
          name="description"
          label={strings.fields.description.label}
          value={spareForm.description}
          onChange={onChange}
        ></TextAreaField>

        <Flex justifyContent="end">
          <Button
            children={strings.fields.buttons.reset}
            onClick={resetFormValues}
          ></Button>
          <Button
            children={
              type === "create"
                ? strings.fields.buttons.create
                : strings.fields.buttons.save
            }
            onClick={onSubmitForm}
            variation="primary"
            isDisabled={Object.values(errors).some((e) => !!e)}
          ></Button>
        </Flex>
      </Grid>
    </>
  );
};

const styles = {
  autocomplete: {
    rowGap: "8px",
  },
};

const strings = new LocalizedStrings({
  es: {
    fields: {
      name: {
        label: "Nombre",
      },
      location: {
        label: "Ubicación",
      },
      description: {
        label: "Descripción",
      },
      status: {
        label: "Estado",
      },
      company: {
        label: "Empresa",
        placeholder: "Buscar empresa...",
      },
      serviceOrder: {
        label: "Orden de Servicio",
        placeholder: "Buscar orden de servicio...",
      },
      equipment: {
        label: "Equipo",
        placeholder: "Buscar equipo...",
      },
      buttons: {
        create: "Crear",
        save: "Guardar",
        reset: "Restablecer",
      },
    },
    validations: {
      incorrectValue: "Valor incorrecto",
    },
    responses: {
      errorFetchCompanies: "Error al consultar empresas",
      errorFetchServiceOrders: "Error al consultar órdenes de servicio",
      errorFetchEquipments: "Error al consultar equipos",
    },
  },
});

export default SpareForm;
